import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export interface PedagogyReviewSkipInsightsSummary {
  assignedCount: number
  doneCount: number
  skippedCount: number
  awaitingVerificationCount: number
  developerIssuesCount: number
}

export interface PedagogyReviewSkipSpecialistRow {
  specialistUserId: string
  specialistName: string
  assignedCount: number
  doneCount: number
  skippedCount: number
  skippedPercent: number
  recordingProblemCount: number
  noAccessCount: number
  noCameraCount: number
  lessonMismatchCount: number
  queueSelectionErrorCount: number
  confirmedCount: number
  changedCount: number
  notConfirmedCount: number
  awaitingVerificationCount: number
}

export interface PedagogyReviewSkipPartnerRow {
  partnerNumber: number | null
  partnerName: string
  skippedCount: number
  uniqueProblemCount: number
  affectedStudiosCount: number
  recordingProblemCount: number
  noAccessCount: number
  noCameraCount: number
  lessonMismatchCount: number
  queueSelectionErrorCount: number
  openCasesCount: number
  oldestOpenAt: string | null
}

export interface PedagogyReviewDeveloperIssue {
  caseId: number
  studioNumber: string
  cityName: string | null
  partnerNumber: number | null
  partnerName: string
  trainingDate: string
  trainingTime: string
  directionName: string | null
  teacherName: string | null
  details: string | null
  decisionComment: string | null
  // exclude_lesson_request_studio — руководитель попросил убрать и студию.
  queueAction: string | null
  reportedByName: string | null
  decidedByName: string | null
  decidedAt: string
}

export interface PedagogyReviewSkipInsights {
  rangeStart: string
  rangeEnd: string
  summary: PedagogyReviewSkipInsightsSummary
  specialists: PedagogyReviewSkipSpecialistRow[]
  partners: PedagogyReviewSkipPartnerRow[]
  developerIssues: PedagogyReviewDeveloperIssue[]
}

type JsonRecord = Record<string, unknown>

function recordValue(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {}
}

function numberValue(value: unknown): number {
  return Number(value ?? 0)
}

// Студия без однозначного франчайзи приходит с partner_number = null, а не 0.
function nullableNumber(value: unknown): number | null {
  return value == null ? null : Number(value)
}

function nullableText(value: unknown): string | null {
  return value == null ? null : String(value)
}

export async function listAcknowledgedPedagogyReviewQueueErrorIds(): Promise<number[]> {
  const { data, error } = await withAuthRecovery(() =>
    getSupabase().rpc('list_acknowledged_pedagogy_review_queue_errors')
  )
  if (error) throw new Error(error.message ?? 'Не удалось загрузить учтённые ошибки')
  return Array.isArray(data) ? data.map(row => Number((row as Record<string, unknown>).case_id)).filter(Number.isFinite) : []
}

export async function acknowledgePedagogyReviewQueueError(decisionId: number, comment: string): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    getSupabase().rpc('acknowledge_pedagogy_review_queue_error', { p_decision_id: decisionId, p_comment: comment })
  )
  if (error) throw new Error(error.message ?? 'Не удалось отметить ошибку')
}

export async function getPedagogyReviewSkipInsights(input: {
  periodId: number
  weekNumber?: number | null
}): Promise<PedagogyReviewSkipInsights> {
  const { data, error } = await withAuthRecovery(() =>
    getSupabase().rpc('get_pedagogy_review_skip_insights', {
      p_period_id: input.periodId,
      p_week_number_in_period: input.weekNumber ?? null,
    })
  )
  if (error) throw new Error(error.message ?? 'Не удалось загрузить статистику пропусков')

  const payload = recordValue(data)
  const summary = recordValue(payload.summary)
  return {
    rangeStart: String(payload.rangeStart ?? ''),
    rangeEnd: String(payload.rangeEnd ?? ''),
    summary: {
      assignedCount: numberValue(summary.assignedCount),
      doneCount: numberValue(summary.doneCount),
      skippedCount: numberValue(summary.skippedCount),
      awaitingVerificationCount: numberValue(summary.awaitingVerificationCount),
      developerIssuesCount: numberValue(summary.developerIssuesCount),
    },
    specialists: Array.isArray(payload.specialists) ? payload.specialists.map(value => {
      const row = recordValue(value)
      return {
        specialistUserId: String(row.specialist_user_id ?? ''),
        specialistName: String(row.specialist_name ?? 'Без имени'),
        assignedCount: numberValue(row.assigned_count),
        doneCount: numberValue(row.done_count),
        skippedCount: numberValue(row.skipped_count),
        skippedPercent: numberValue(row.skipped_percent),
        recordingProblemCount: numberValue(row.recording_problem_count),
        noAccessCount: numberValue(row.no_access_count),
        noCameraCount: numberValue(row.no_camera_count),
        lessonMismatchCount: numberValue(row.lesson_mismatch_count),
        queueSelectionErrorCount: numberValue(row.queue_selection_error_count),
        confirmedCount: numberValue(row.confirmed_count),
        changedCount: numberValue(row.changed_count),
        notConfirmedCount: numberValue(row.not_confirmed_count),
        awaitingVerificationCount: numberValue(row.awaiting_verification_count),
      }
    }) : [],
    partners: Array.isArray(payload.partners) ? payload.partners.map(value => {
      const row = recordValue(value)
      return {
        partnerNumber: nullableNumber(row.partner_number),
        partnerName: String(row.partner_name ?? 'Франчайзи не определён'),
        skippedCount: numberValue(row.skipped_count),
        uniqueProblemCount: numberValue(row.unique_problem_count),
        affectedStudiosCount: numberValue(row.affected_studios_count),
        recordingProblemCount: numberValue(row.recording_problem_count),
        noAccessCount: numberValue(row.no_access_count),
        noCameraCount: numberValue(row.no_camera_count),
        lessonMismatchCount: numberValue(row.lesson_mismatch_count),
        queueSelectionErrorCount: numberValue(row.queue_selection_error_count),
        openCasesCount: numberValue(row.open_cases_count),
        oldestOpenAt: nullableText(row.oldest_open_at),
      }
    }) : [],
    developerIssues: Array.isArray(payload.developerIssues) ? payload.developerIssues.map(value => {
      const row = recordValue(value)
      return {
        caseId: numberValue(row.case_id),
        studioNumber: String(row.studio_number ?? ''),
        cityName: nullableText(row.city_name),
        partnerNumber: nullableNumber(row.partner_number),
        partnerName: String(row.partner_name ?? 'Франчайзи не определён'),
        trainingDate: String(row.training_date ?? ''),
        trainingTime: String(row.training_time ?? ''),
        directionName: nullableText(row.direction_name_raw),
        teacherName: nullableText(row.teacher_name_raw),
        details: nullableText(row.details),
        decisionComment: nullableText(row.decision_comment),
        queueAction: nullableText(row.queue_action),
        reportedByName: nullableText(row.reported_by_name),
        decidedByName: nullableText(row.decided_by_name),
        decidedAt: String(row.decided_at ?? ''),
      }
    }) : [],
  }
}
